import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { HeaderBar } from '../components/HeaderBar.tsx';
import { useMe } from '../api/hooks.ts';
import {
  useSleep,
  useFeed,
  usePump,
  useDiaper,
  useDeleteSleep,
  useDeleteFeed,
  useDeletePump,
  useDeleteDiaper,
  useReopenSleep,
  useReopenFeed,
  useUpdatePump,
} from '../api/events.ts';
import { fmtTime, fmtDur } from '../lib/format.ts';
import type { AnyEvent, User } from '@shared/types.ts';
import { EventDetailSheet } from '../components/sheets/EventDetailSheet.tsx';
import { EventEditSheet } from '../components/sheets/EventEditSheet.tsx';
import { Icon } from '../components/Icon.tsx';
import { summarise, summaryLabel } from '../lib/breast.ts';

const DAY = 86400;

export function Events() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  const usersById: Record<string, User> = useMemo(
    () => Object.fromEntries((me?.members ?? []).map((m) => [m.id, m])),
    [me?.members],
  );

  // 30-day window, bucketed to the minute so the query key is stable.
  const w = useMemo(() => {
    const to = Math.floor(Date.now() / 60_000) * 60 + 3600;
    return { from: to - 30 * DAY, to, babyId: baby?.id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby?.id, Math.floor(Date.now() / 60_000)]);
  const sleep = useSleep(w).data ?? [];
  const feed = useFeed(w).data ?? [];
  const pump = usePump(w).data ?? [];
  const diaper = useDiaper(w).data ?? [];

  const grouped = useMemo(() => {
    const all: AnyEvent[] = [
      ...sleep.map((x) => ({ _t: 'sleep' as const, ...x })),
      ...feed.map((x) => ({ _t: 'feed' as const, ...x })),
      ...pump.map((x) => ({ _t: 'pump' as const, ...x })),
      ...diaper.map((x) => ({ _t: 'diaper' as const, ...x })),
    ];
    all.sort((a, b) => atOf(b) - atOf(a));
    const map = new Map<string, AnyEvent[]>();
    for (const e of all) {
      const key = dayKeyLocal(atOf(e));
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return Array.from(map.entries()); // already in DESC order
  }, [sleep, feed, pump, diaper]);

  const [selected, setSelected] = useState<AnyEvent | null>(null);
  const [editing, setEditing] = useState<AnyEvent | null>(null);
  const delSleep = useDeleteSleep();
  const delFeed = useDeleteFeed();
  const delPump = useDeletePump();
  const delDiaper = useDeleteDiaper();
  const reopenSleep = useReopenSleep();
  const reopenFeed = useReopenFeed();
  const updatePump = useUpdatePump();

  async function handleDelete(e: AnyEvent) {
    try {
      if (e._t === 'sleep') await delSleep.mutateAsync(e.id);
      else if (e._t === 'feed') await delFeed.mutateAsync(e.id);
      else if (e._t === 'pump') await delPump.mutateAsync(e.id);
      else await delDiaper.mutateAsync(e.id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function handleResume(e: AnyEvent) {
    try {
      if (e._t === 'sleep') await reopenSleep.mutateAsync(e.id);
      else if (e._t === 'feed') await reopenFeed.mutateAsync(e.id);
      else if (e._t === 'pump')
        await updatePump.mutateAsync({ id: e.id, patch: { ended_at: null } });
      else return;
      toast.success('Resumed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Resume failed');
    }
  }

  return (
    <>
      <HeaderBar title="Events" subtitle="last 30 days" baby={baby} />
      <div className="lt-scroll" style={{ flex: 1, padding: '0 0 24px' }}>
        {grouped.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
            No events yet.
          </div>
        )}
        {grouped.map(([dayKey, items]) => (
          <DaySection
            key={dayKey}
            dayKey={dayKey}
            items={items}
            usersById={usersById}
            onSelect={setSelected}
          />
        ))}
      </div>

      <EventDetailSheet
        open={!!selected}
        event={selected}
        onClose={() => setSelected(null)}
        onDelete={handleDelete}
        onResume={handleResume}
        onEdit={(e) => {
          setSelected(null);
          setEditing(e);
        }}
      />
      <EventEditSheet
        open={!!editing}
        event={editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

function DaySection({
  dayKey,
  items,
  usersById,
  onSelect,
}: {
  dayKey: string;
  items: AnyEvent[];
  usersById: Record<string, User>;
  onSelect: (e: AnyEvent) => void;
}) {
  const date = new Date(`${dayKey}T00:00:00`);
  const today = dayKeyLocal(Math.floor(Date.now() / 1000));
  const isToday = dayKey === today;
  const yesterday = dayKeyLocal(Math.floor(Date.now() / 1000) - 86400);
  const isYesterday = dayKey === yesterday;
  const label = isToday
    ? 'Today'
    : isYesterday
      ? 'Yesterday'
      : date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          padding: '6px 20px 8px',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          letterSpacing: 0.04,
          textTransform: 'uppercase',
          position: 'sticky',
          top: 0,
          background: 'var(--bg-app)',
          zIndex: 2,
        }}
      >
        {label}
      </div>
      <div style={{ padding: '0 16px' }}>
        <div
          className="lt-card"
          style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          {items.map((e, i) => (
            <EventRow
              key={e.id}
              e={e}
              who={usersById[e.created_by]}
              isLast={i === items.length - 1}
              onClick={() => onSelect(e)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function EventRow({
  e,
  who,
  isLast,
  onClick,
}: {
  e: AnyEvent;
  who?: User;
  isLast: boolean;
  onClick: () => void;
}) {
  const meta = describe(e);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: isLast ? 'none' : '1px solid var(--divider)',
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 999,
          background: meta.color,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {meta.label}
          {hasNotes(e) && (
            <span
              title="Has a note"
              style={{
                color: 'var(--text-3)',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Icon.edit s={12} />
            </span>
          )}
        </div>
        {meta.detail && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{meta.detail}</div>
        )}
      </div>
      <span className="lt-mono lt-tabular" style={{ fontSize: 12, color: 'var(--text-3)' }}>
        {fmtTime(atOf(e))}
      </span>
      {who && (
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            background: who.color ?? 'var(--accent)',
            color: 'white',
            fontSize: 9,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {who.name[0]?.toUpperCase()}
        </span>
      )}
    </button>
  );
}

function atOf(e: AnyEvent): number {
  return e._t === 'diaper' ? e.occurred_at : e.started_at;
}

function hasNotes(e: AnyEvent): boolean {
  return typeof (e as { notes?: string | null }).notes === 'string' &&
    !!(e as { notes?: string | null }).notes?.trim();
}

function dayKeyLocal(sec: number): string {
  const d = new Date(sec * 1000);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function describe(e: AnyEvent): { color: string; label: string; detail?: string } {
  if (e._t === 'sleep') {
    const end = e.ended_at ?? Math.floor(Date.now() / 1000);
    const mins = Math.max(0, Math.floor((end - e.started_at) / 60));
    return {
      color: 'var(--sleep-day)',
      label: e.location ? `Sleep · ${e.location}` : 'Sleep',
      detail: `${fmtDur(mins)}${e.ended_at == null ? ' · in progress' : ''}`,
    };
  }
  if (e._t === 'feed') {
    if (e.type === 'bottle') {
      return {
        color: 'var(--bottle)',
        label: 'Bottle',
        detail: e.amount_ml ? `${e.amount_ml} ml` : undefined,
      };
    }
    if (e.type === 'solid') return { color: 'var(--solid)', label: 'Solid' };
    const mins = e.ended_at
      ? Math.max(1, Math.floor((e.ended_at - e.started_at) / 60))
      : null;
    const s = summarise(e);
    const summary = s.hasHistory ? summaryLabel(s, { showPaused: true }) : null;
    return {
      color: 'var(--breast)',
      label: 'Breast feed',
      detail: [
        mins != null ? fmtDur(mins) : 'in progress',
        summary || null,
      ]
        .filter(Boolean)
        .join(' · '),
    };
  }
  if (e._t === 'pump') {
    return {
      color: 'var(--pump)',
      label: `Pump${e.side ? ` · ${e.side}` : ''}`,
      detail: e.yield_ml ? `${e.yield_ml} ml` : e.ended_at ? '' : 'in progress',
    };
  }
  // diaper
  const c = e.kind === 'pee' ? 'var(--pee)' : 'var(--poop)';
  const extras = [e.consistency, e.color, e.leak ? 'leak' : null].filter(Boolean).join(' · ');
  return {
    color: c,
    label: `Diaper · ${e.kind}`,
    detail: extras || undefined,
  };
}
